import * as React from "react";
import { useContext } from "react";

import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import BackupIcon from "@mui/icons-material/Backup";
import Typography from "@mui/material/Typography";
import CardMedia from "@mui/material/CardMedia";
import Box from "@mui/material/Box";
import FaceRetouchingNaturalIcon from "@mui/icons-material/FaceRetouchingNatural";

import AttributeSelectorGroup from "../../components/AttributeSelectorGroup";
import { updatePersonCharacteristics } from "../../services/api/personService";
import { CognitoUserContext } from "../../contexts/cognitoUserContext.js";
import { canEditPerson } from "../../services/permissions";
import { useSearch } from "../../contexts/searchContext";
import { GenerateCharacteristics } from "./CharacteristicsGenerator";
import { CHARACTERISTICS_METADATA, characteristicsMapToPerson, personToCharacteristicsMap } from '@gamma/shared/attributes';
function SimpleDialog(props) {
  const { onClose, open, person, characteristicsSchema } = props;
  let p = person.person;

  // Use object state instead of Map for easier integration with AttributeSelectorGroup
  const [attributeValues, setAttributeValues] = React.useState(() => {
    const values = {};
    Object.keys(CHARACTERISTICS_METADATA).forEach((attributeType) => {
      const dbField = CHARACTERISTICS_METADATA[attributeType].dbField;
      values[attributeType] = p[dbField];
    });
    return values;
  });

  const handleClose = () => {
    onClose();
  };

  // Here we handle the api call to update the person
  const handleUpdate = async () => {
    // Convert attributeValues to database field names
    const characteristics = {};
    Object.entries(attributeValues).forEach(([attributeType, value]) => {
      const metadata = CHARACTERISTICS_METADATA[attributeType];
      if (metadata && value !== undefined) {
        characteristics[metadata.dbField] = value;
      }
    });

    try {
      await updatePersonCharacteristics(p.person_id, characteristics);
      console.log(`Updated characteristics for ${p.person_id}:`, characteristics);
      onClose();
    } catch (error) {
      console.error('Failed to update person characteristics:', error);
      alert(`Failed to save changes: ${error.message}`);
    }
  };

  function handleAttributeChange(attributeType, newValue) {
    setAttributeValues(prev => ({
      ...prev,
      [attributeType]: newValue
    }));
  }


  return (
    <Dialog
      onClose={handleClose}
      open={open}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { height: '80vh', display: 'flex', flexDirection: 'column' }
      }}
    >
      {/* Fixed Header: Image + Name + Save Button */}
      <Box sx={{ p: 3, pb: 2, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <CardMedia
            component="img"
            sx={{
              width: 120,
              height: 120,
              borderRadius: 1,
              objectFit: 'cover'
            }}
            image={p.image}
            alt={`${p.first_name} ${p.last_name}`}
          />

          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              {p.first_name} {p.last_name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {p.external_id}
            </Typography>
          </Box>

          <IconButton
            onClick={handleUpdate}
            color="success"
            sx={{
              bgcolor: 'success.light',
              '&:hover': { bgcolor: 'success.main', color: 'white' }
            }}
          >
            <BackupIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Scrollable Characteristics List */}
      <Box
        sx={{
          px: 3,
          pb: 3,
          pt: 1,
          flexGrow: 1,
          overflowY: 'auto'
        }}
      >
        <AttributeSelectorGroup
          attributeValues={attributeValues}
          onChange={handleAttributeChange}
          availableAttributes={characteristicsSchema}
          iconSize={40}
        />
      </Box>
    </Dialog>
  );
}

export default function CharacteristicsEditor({ person, open: externalOpen, onClose: externalOnClose, skipPermissionCheck = false }) {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const { cognitoUser } = useContext(CognitoUserContext);
  const { selectedDataset } = useSearch();
  const characteristicsSchema = GenerateCharacteristics(selectedDataset);

  // Normalize person object - if it's already wrapped, use as-is, otherwise wrap it
  const personObj = person.person ? person : { person };
  const m = personToCharacteristicsMap(personObj.person);
  const canEdit = skipPermissionCheck || canEditPerson(cognitoUser);

  // Use external props if provided, otherwise use internal state
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const isControlled = externalOpen !== undefined;

  console.log('[CharacteristicsEditor] Render state:', {
    person: personObj,
    open,
    canEdit,
    skipPermissionCheck,
    cognitoUser,
    selectedDataset,
    characteristicsSchema
  });

  if (!canEdit) {
    console.warn('[CharacteristicsEditor] Cannot edit - canEditPerson returned false');
    return null;
  }

  //const [selectedValue, setSelectedValue] =
  // const [selectedValue, setSelectedValue] = React.useState(map);
  const handleClickOpen = () => {
    if (!isControlled) {
      setInternalOpen(true);
    }
  };
  const handleClose = (value) => {
    if (isControlled && externalOnClose) {
      externalOnClose(value);
    } else {
      setInternalOpen(false);
    }
  };

  // If controlled externally, just render dialog without button
  if (isControlled) {
    return (
      <SimpleDialog
        person={personObj}
        map={m}
        open={open}
        onClose={handleClose}
        characteristicsSchema={characteristicsSchema}
      />
    );
  }

  // Otherwise render button + dialog (original behavior)
  return (
    <div>
      <IconButton
        variant="outlined"
        sx={{
          float: "right",
          borderRadius: 10,
          display: "flex",
          gap: 0,
          height: 20,
          fontSize: "0.7rem",
          fontWeight: "bold",
          letterSpacing: ".05rem",
          textTransform: "uppercase",
          color: "#2d3e50",
        }}
        onClick={handleClickOpen}
      >
        <FaceRetouchingNaturalIcon />
      </IconButton>
      <SimpleDialog
        person={personObj}
        map={m}
        open={open}
        onClose={handleClose}
        characteristicsSchema={characteristicsSchema}
      />
    </div>
  );
}
